import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);

  const menuItems = [
    { label: 'Forside', href: '/' },
    { label: 'Køletransport', href: '/koeletransport' },
    { label: 'Om os', href: '/om-os' },
    { label: 'Kontakt', href: '/kontakt' }
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-brand-dark/95 backdrop-blur-md border-b border-brand-dark-border">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="flex h-24 items-center justify-between">
          
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center gap-3 group" onClick={() => setIsOpen(false)}>
              <img 
                src="https://assets.auxodev.dk/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/logo.svg" 
                alt="Chr. Nielsen & Søn" 
                className="h-12 max-w-[180px] object-contain transition-transform duration-300 group-hover:scale-105 invert brightness-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden font-bold text-lg tracking-wider text-white uppercase leading-tight">
                Chr. Nielsen <span className="text-brand-red">& Søn</span>
                <div className="text-[10px] tracking-[0.2em] text-brand-green font-medium">V/Leif Nielsen</div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation (Visible >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            {menuItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) => 
                  `relative py-2 text-[15px] font-semibold uppercase tracking-wider transition-colors duration-200 hover:text-brand-red ${
                    isActive ? 'text-brand-red' : 'text-gray-300'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 h-[2px] w-full bg-brand-red" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Contact Info (Visible >= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            {/* Telefonboks */}
            <a 
              href="tel:40535984" 
              className="flex items-center gap-3 px-5 py-3 bg-brand-red hover:bg-brand-red-hover text-white rounded-md font-bold text-[15px] tracking-wide transition-all duration-200 shadow-lg shadow-brand-red/10 hover:scale-[1.02] active:scale-[0.98]"
            >
              <i className="fa-solid fa-phone text-lg animate-pulse" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider text-red-200 font-medium leading-none">Ring nu (Primær)</div>
                <div className="whitespace-nowrap leading-tight">40 53 59 84</div>
              </div>
            </a>

            {/* Mailboks */}
            <a 
              href="mailto:info@chrnielsen.dk" 
              className="flex items-center gap-3 px-5 py-3 bg-brand-dark-card hover:bg-brand-dark-border border border-brand-green/30 hover:border-brand-green text-white rounded-md font-bold text-[15px] tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <i className="fa-solid fa-envelope text-lg text-brand-green" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium leading-none">E-mail</div>
                <div className="whitespace-nowrap text-brand-green leading-tight">info@chrnielsen.dk</div>
              </div>
            </a>
          </div>

          {/* Mobile Menu Button (Visible < 1200px) */}
          <div className="flex min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2.5 text-gray-400 hover:bg-brand-dark-card hover:text-white transition-colors duration-200"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Åbn hovedmenu</span>
              {isOpen ? (
                <i className="fa-solid fa-xmark text-2xl w-6 h-6 flex items-center justify-center" />
              ) : (
                <i className="fa-solid fa-bars text-2xl w-6 h-6 flex items-center justify-center" />
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu (Visible < 1200px) */}
      <div 
        className={`min-[1200px]:hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[500px] opacity-100 border-t border-brand-dark-border' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
        id="mobile-menu"
      >
        <div className="space-y-1 px-4 py-4 bg-brand-dark-card">
          {menuItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) => 
                `block rounded-md px-4 py-3 text-base font-semibold uppercase tracking-wider transition-colors ${
                  isActive 
                    ? 'bg-brand-red/10 text-brand-red border-l-4 border-brand-red' 
                    : 'text-gray-300 hover:bg-brand-dark-border hover:text-white'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {/* Mobile Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-brand-dark-border mt-4">
            {/* Telefonboks */}
            <a 
              href="tel:40535984" 
              className="flex items-center justify-center gap-3 px-4 py-4 bg-brand-red hover:bg-brand-red-hover text-white rounded-md font-bold text-base tracking-wide transition-colors"
            >
              <i className="fa-solid fa-phone text-lg" />
              <span className="whitespace-nowrap">40 53 59 84</span>
            </a>

            {/* Mailboks */}
            <a 
              href="mailto:info@chrnielsen.dk" 
              className="flex items-center justify-center gap-3 px-4 py-4 bg-brand-dark border border-brand-green/30 hover:border-brand-green text-white rounded-md font-bold text-base tracking-wide transition-colors"
            >
              <i className="fa-solid fa-envelope text-lg text-brand-green" />
              <span className="whitespace-nowrap text-brand-green">info@chrnielsen.dk</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
